import { useState, useEffect } from "react";
import { Tabs, Box, ScrollArea, Divider, Text, Image, Stack, Center } from "@mantine/core";
import styles from "./personality.report.module.css";
import MarkViewer from "@/componets/mark.viewer";
import { NumberLine } from "@/componets/NumberLine";
import { NumberLinePropPresets } from "@/presets.datashaps";
import { type OEJTresult } from "@/q-n-a";
import { useResults } from "../contexts/results.ctx";
// SVG imports
import Icon1 from '../assets/1-square-list-regular.svg';
import Icon2 from '../assets/2-brain-circuit-regular.svg';
import Icon3 from '../assets/3-puzzle-regular.svg';
import Icon4 from '../assets/4-list-check-regular.svg';
import Icon5 from '../assets/5-file-chart-pie-regular.svg';
import Icon6 from '../assets/6-calendar-star-regular.svg';

function CreatePersonalityReportObj(_clientName: string, clientType: string, agentType: string) {
    // Ensure clientType and agentType end with '-A' or '-T', and if not default to '-A'
    if (!(clientType.endsWith('-A') || clientType.endsWith('-T'))) {
        clientType = clientType + '-A';
    }
    if (!(agentType.endsWith('-A') || agentType.endsWith('-T'))) {
        agentType = agentType + '-A';
    }
    return {
        summaryObj: {
            md: `agent_client/${agentType}_${clientType}_1a_summary_text.md`,
            overview: `personal_breakdown/${clientType}_1b_personality_overview.md`
        },
        pbs: [`personal_breakdown/${clientType}_2a_personality_analysis.md`],
        compat: [`agent_client/${agentType}_${clientType}_3a_compatability_breakdown.md`],
        dds: [`agent_client/${agentType}_${clientType}_4a_communication_recommendations.md`],
        infosheet: [
            `personal_breakdown/${clientType}_5a_info_sheet_preference.md`,
            `personal_breakdown/${clientType}_5b_info_sheet_example.md`
        ],
        followup: [
            `personal_breakdown/${clientType}_6a_follow_up_schedule.md`,
            `personal_breakdown/${clientType}_6b_gift_recommendations.md`
        ],
    }
}

type PersonalityReportObj = ReturnType<typeof CreatePersonalityReportObj>;

export interface PersonalityReportProps { clientName: string, clientType: string, agentType: string, testID: string }

const TypeMatcher = {
    'poor': "More Challenging Fit",
    'decent': "Generally Compatible",
    'great': "Best-Fit Client Type"
} as const;

const matchStrengthNodes = {
    'poor': '⚠️ More Challenging Fit Client Types',
    'decent': '⚖️ Generally Compatible Client Types',
    'great': '✅ Best-Fit Client Types',
}

const iconMap: Record<number, string> = {
    1: Icon1,
    2: Icon2,
    3: Icon3,
    4: Icon4,
    5: Icon5,
    6: Icon6,
}

const txtMap: Record<number, string> = {
    1: 'Summary',
    2: 'Breakdown',
    3: 'Compatibility',
    4: 'Communication',
    5: 'Info Sheet',
    6: 'Follow Up'
}

export default function PersonalityReport({ clientName, clientType, agentType, testID }: PersonalityReportProps) {
    const [reportObj, setReportObj] = useState<PersonalityReportObj | undefined>(undefined);
    const [breakdown, setBreakdown] = useState<OEJTresult | null>(null);
    const [matchStrength, setMatchStrength] = useState<keyof typeof TypeMatcher | null>(null);
    const { getMarkdownTxt, getResultScores } = useResults();
    useEffect(() => {
        const rptObj = CreatePersonalityReportObj(clientName, clientType, agentType);
        setReportObj(rptObj);
        if (testID) {
            getResultScores(testID)
                .then(setBreakdown)
                .catch(console.error)
                .finally(async () => {
                    // if (!reportObj) {
                    //     console.error('No report object found...');
                    //     return;
                    // }
                    const rawMd = await getMarkdownTxt(rptObj.compat[0]);
                    Object.entries(TypeMatcher).forEach(([key, value]) => {
                        if (rawMd.includes(value)) {
                            setMatchStrength(key as keyof typeof TypeMatcher);
                        }
                    });
                    setReportObj(prev => prev ? {
                        ...prev,
                        pbs: [prev.pbs[0] ?? '', rawMd]
                    } : undefined);
                })
        }
    }, [clientName, clientType, agentType, testID]);

    // useEffect(() => {
    //     console.log('some info', iconMap[1])
    //     if (testID) {
    //         getResultScores(testID)
    //             .then(setBreakdown)
    //             .catch(console.error)
    //             .finally(async () => {
    //                 if (!reportObj) {
    //                     console.error('No report object found...');
    //                     return;
    //                 }
    //                 const rawMd = await getMarkdownTxt(reportObj.compat[0]);
    //                 Object.entries(TypeMatcher).forEach(([key, value]) => {
    //                     if (rawMd.includes(value)) {
    //                         setMatchStrength(key as keyof typeof TypeMatcher);
    //                     }
    //                 });
    //                 setReportObj(prev => prev ? {
    //                     ...prev,
    //                     pbs: [prev.pbs[0] ?? '', rawMd]
    //                 } : undefined);
    //             })
    //     }
    // }, [testID]);

    if (!reportObj) return null;

    return (
        <Box className={styles.container}>
            <Tabs defaultValue="summaryObj" className={styles.tabs}>
                <ScrollArea.Autosize mah='70vh'>
                    {Object.entries(reportObj).map(([key, ids]) => {
                        if (!Array.isArray(ids)) {
                            return (
                                <Tabs.Panel value={key} className={styles.tabPanel}>
                                    <MarkViewer id={ids.md} markdownFetchFn={getMarkdownTxt} />
                                    {Object.entries(breakdown || {}).map(([key, value], index) => (
                                        <NumberLine key={`${key}-${index}`} {...NumberLinePropPresets[key as keyof typeof NumberLinePropPresets]} value={value} />
                                    ))}
                                    <Divider />
                                    <MarkViewer id={ids.overview} markdownFetchFn={getMarkdownTxt} />
                                    <Center py={'lg'}>
                                        <Text c={'dimmed'} size="1.2rem">falls into your</Text>
                                        <Text fw={800} size={'1.2rem'}>
                                            {matchStrength && matchStrengthNodes[matchStrength]}
                                        </Text>
                                    </Center>
                                </Tabs.Panel>
                            )
                        }
                        return (
                            <Tabs.Panel value={key} className={styles.tabPanel}>
                                {ids.map((id, dex, slf) => {
                                    if(!!slf[dex + 1] && id.endsWith('.md') && !(slf[dex+1].endsWith('.md'))) {
                                        return <MarkViewer id={id} rawMd={slf[dex+1]} markdownFetchFn={getMarkdownTxt} />
                                    }
                                    if(!id.endsWith('.md')) return null;
                                    return <MarkViewer id={id} markdownFetchFn={getMarkdownTxt} />;
                                })}
                            </Tabs.Panel>
                        )
                    })}
                </ScrollArea.Autosize>

                <Tabs.List className={styles.tabsList} grow>
                    {Object.keys(reportObj).map((k, dex) => <Tabs.Tab value={k}>
                        <Stack>
                            <Image src={iconMap[dex+1]} alt={k.toUpperCase()} h={20} w={'auto'} fit='contain' m={'auto'} />
                            <Text size={'0.65rem'}>{txtMap[dex+1]}</Text>
                        </Stack>
                    </Tabs.Tab>)}
                </Tabs.List>
            </Tabs>
        </Box>
    );
} 